import React, { useState, useEffect } from "react";
import Sidebar from "../sidebar/sidebar";
import "./Dashboard.css";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
} from "chart.js";
import { getDashboardData } from "../../hooks/api/utils";
import { format } from "date-fns";
import { Link } from "react-router-dom";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderTab, setOrderTab] = useState("recent");
  const [salesView, setSalesView] = useState("monthly");

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

const generateColors = (num) => {
  const colors = [];
  for (let i = 0; i < num; i++) {
    const hue = (i * 221.5) % 360; 
    colors.push(`hsl(${hue}, 50%, 60%)`); 
  }
  return colors;
};

  const donutLabels =
    dashboardData?.vendor_services?.service_distribution || [];

  const donutColors = generateColors(donutLabels.length);

  const donutData = {
    datasets: [
      {
        data: donutLabels.map((s) => s.percentage),
        backgroundColor: donutColors,
        borderWidth: 1,
      },
    ],
  };
  // Sales Chart Data (Dynamic)
  const monthlyLabels =
    dashboardData?.current_month_year_sales?.current_month?.map((item) =>
      format(new Date(item.day), "dd MMM")
    ) || [];

  const monthlyData =
    dashboardData?.current_month_year_sales?.current_month?.map(
      (item) => item.order_count
    ) || [];

  const yearlyLabels = months.map((m) => m.slice(0, 3).toUpperCase());
  const yearlyData = months.map(
    (m) =>
      dashboardData?.current_month_year_sales?.current_year?.[m]?.order_count ||
      0
  );

  const salesData = {
    labels: salesView === "monthly" ? monthlyLabels : yearlyLabels,
    datasets: [
      {
        label: salesView === "monthly" ? "Monthly" : "Yearly",
        data: salesView === "monthly" ? monthlyData : yearlyData,
        backgroundColor:
          salesView === "monthly"
            ? "rgba(0, 90, 255, 0.2)"
            : "rgba(255, 99, 132, 0.2)",
        borderColor:
          salesView === "monthly"
            ? "rgba(0, 90, 255, 0.5)"
            : "rgba(255, 99, 132, 0.4)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
      },
    ],
  };

  const optionsSales = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          padding: 15,
          color: "#666",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        grid: {
          drawBorder: false,
          color: "#eee",
        },
      },
    },
  };

  // Revenue Bar Chart
  const revenueData = {
    labels: months.map((m) => m.slice(0, 3).toUpperCase()),
    datasets: [
      {
        label: "Ordered",
        data: months.map((m) => dashboardData?.monthly_orders?.[m]?.total || 0),
        backgroundColor: "#d1f2fb",
        borderRadius: 6,
        barThickness: 12,
        stack: "stack1",
      },
      {
        label: "Completed",
        data: months.map(
          (m) => dashboardData?.monthly_orders?.[m]?.completed || 0
        ),
        backgroundColor: "#00c49a",
        borderRadius: 6,
        barThickness: 12,
        stack: "stack1",
      },
      {
        label: "Cancelled",
        data: months.map(
          (m) => dashboardData?.monthly_orders?.[m]?.cancelled || 0
        ),
        backgroundColor: "#ff6f61",
        borderRadius: 6,
        barThickness: 12,
        stack: "stack1",
      },
    ],
  };

  const optionsRevenue = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          padding: 20,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
      },
      y: {
        stacked: true,
        grid: {
          drawBorder: false,
          color: "#eee",
        },
      },
    },
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  useEffect(() => {
    getDashboardData("/admin-panel/dashboard/", setLoading, setDashboardData);
  }, []);

  const getOrdersByTab = () => {
    if (orderTab === "ongoing") return dashboardData?.ongoing_orders || [];
    if (orderTab === "settled") return dashboardData?.completed_orders || [];
    return dashboardData?.recent_orders || [];
  };

  return (
      <div className="rightContent right-dashboard-container">
        <div className="viewMain">
          <div className="statsCards">
            <div className="cardBox">
              Vendor Services{" "}
              <h1>
                {dashboardData?.vendor_services?.total_vendor_services || 0}
              </h1>
            </div>
            <div className="cardBox">
              Total Users <h1>{dashboardData?.total_users || 0}</h1>
            </div>
            <div className="cardBox">
              Total Revenue <h1>{dashboardData?.total_revenue.toFixed(2) || 0}</h1>
            </div>
            <div className="cardBox">
              Total Sales <h1>{dashboardData?.total_orders || 0}</h1>
            </div>
          </div>

          <div className="chartAndOrders">
            <div className="donutBox">
              <h5>Vendor services</h5>
              <div style={{ width: "100%", height: "220px", margin: "0 auto" }}>
                <Doughnut data={donutData} options={donutOptions} />
              </div>
              <div className="legend">
                {donutLabels.map((item, i) => (
                  <span key={i}>
                    <span
                      className="dot"
                      style={{ backgroundColor: donutColors[i] }}
                    />
                    {item.service_name || `Service ${i + 1}`}
                  </span>
                ))}
              </div>
            </div>

            <div className="ordersBox">
              <div className="tabs">
                <button
                  className={`tab ${orderTab === "recent" ? "active" : ""}`}
                  onClick={() => setOrderTab("recent")}
                >
                  Recent Orders
                </button>
                <button
                  className={`tab ${orderTab === "ongoing" ? "active" : ""}`}
                  onClick={() => setOrderTab("ongoing")}
                >
                  Ongoing
                </button>
                <button
                  className={`tab ${orderTab === "settled" ? "active" : ""}`}
                  onClick={() => setOrderTab("settled")}
                >
                  Settled
                </button>
              </div>
              <table className="ordersTable">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    {/* <th>Vendor</th> */}
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {getOrdersByTab().map((order) => (
                    <tr key={order.id}>
                      <td>{order.full_name || "—"}</td>
                      <td>{order.status}</td>
                      {/* <td className="link">NDVI</td> */}
                      <td>
                        {format(new Date(order.created_at), "dd/MM/yyyy")}
                      </td>
                      <td>
                        <Link
                           to={"/order-detail"}
                          state={{ orderId: order.id }}
                          className="docBtn w-25"
                          title="View"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 640 640"
                            width={100}
                            height={20}
                            fill="none"
                          >
                            <path
                              d="M320 96C239.2 96 174.5 132.8 127.4 176.6C80.6 220.1 49.3 272 34.4 307.7C31.1 315.6 31.1 324.4 34.4 332.3C49.3 368 80.6 420 127.4 463.4C174.5 507.1 239.2 544 320 544C400.8 544 465.5 507.2 512.6 463.4C559.4 419.9 590.7 368 605.6 332.3C608.9 324.4 608.9 315.6 605.6 307.7C590.7 272 559.4 220 512.6 176.6C465.5 132.9 400.8 96 320 96zM176 320C176 240.5 240.5 176 320 176C399.5 176 464 240.5 464 320C464 399.5 399.5 464 320 464C240.5 464 176 399.5 176 320zM320 256C320 291.3 291.3 320 256 320C244.5 320 233.7 317 224.3 311.6C223.3 322.5 224.2 333.7 227.2 344.8C240.9 396 293.6 426.4 344.8 412.7C396 399 426.4 346.3 412.7 295.1C400.5 249.4 357.2 220.3 311.6 224.3C316.9 233.6 320 244.4 320 256z"
                              fill="#ffffff"
                            />
                          </svg>
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {getOrdersByTab().length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ textAlign: "center" }}>
                        No orders available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="graphs">
            <div className="graphBox">
              <div
                className="graphHeader"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h5>Sales Overview</h5>
                <select
                  value={salesView}
                  onChange={(e) => setSalesView(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    border: "1px solid #ccc",
                    width: "40%",
                  }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <Line data={salesData} options={optionsSales} />
            </div>
            <div className="graphBox">
              <div className="graphHeader">
                <h5>Revenue Overview</h5>
                {/* <span className="graphDropdown">Month ▼</span> */}
              </div>
              <Bar data={revenueData} options={optionsRevenue} />
            </div>
          </div>
        </div>
      </div>
  );
};

export default Dashboard;
